import {
  FunctionComponent,
  HTMLAttributes,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import Downshift, { GetItemPropsOptions, StateChangeOptions } from 'downshift';
import { trackFeature } from '@/utils/adobeLaunch';
import { assertDefined } from '@/utils/assertDefined';
import { fetchTags } from '@/utils/http/fetchTags';
import { t } from '@/utils/i18n';
import { useDebouncedValue } from '@/hooks/useDebounce';
import { Close } from '@/components/common/icons/Close';
import { Search } from '@/components/common/icons/Search';
import './TagInput.scss';

interface Props {
  inputValue: string;
  onSelect: (tag: string) => void;
}

type GetItemPropsType = (options: GetItemPropsOptions<string>) => HTMLAttributes<HTMLLIElement>;

export const TagInput: FunctionComponent<Props> = (props) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<string[]>([]);
  const abortControllerRef = useRef<AbortController>();
  const getTags = useCallback((term: string) => {
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    fetchTags(term, abortController.signal)
      .then((tags) => setItems(assertDefined(tags)))
      .catch(() => setItems([]));
  }, []);

  const [value, setValue] = useDebouncedValue<string>('', getTags, 300);

  // refetch tags if props change (the user selects a (related) tag)
  useEffect(() => {
    if (props.inputValue) {
      getTags(props.inputValue);
    }
  }, [props.inputValue, getTags]);

  // cancel on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  function handleKeyDown(
    e: KeyboardEvent<HTMLInputElement>,
    index: number | null,
    term: string | null
  ) {
    if (e.key === 'Enter' && (index === -1 || index === null)) {
      search(term || '');
    }
  }

  function search(term: string | null) {
    setValue('');
    trackFeature('design-selection-search', { term }, true);
    props.onSelect(term || '');
    navigate(term ? `/designs?searchTerm=${term}` : '/designs');
  }

  function handleStateChange(changes: StateChangeOptions<string>) {
    if (changes.inputValue && !changes.selectedItem) {
      setValue(changes.inputValue);
    }
  }

  function Items(getItemProps: GetItemPropsType, highlightedIndex: number | null) {
    if (!items || !items.length) {
      return null;
    }

    return items.map((item, index) => {
      const classes = cn('tag-input__item', { active: highlightedIndex === index });
      return (
        // getItemProps returns a key
        // eslint-disable-next-line react/jsx-key
        <li
          {...getItemProps({
            key: item,
            index,
            item,
          })}
          className={classes}
        >
          {item}
        </li>
      );
    });
  }

  return (
    <Downshift
      onChange={search}
      selectedItem={value}
      onStateChange={handleStateChange}
      initialInputValue={props.inputValue}
    >
      {({ getInputProps, getMenuProps, getItemProps, isOpen, highlightedIndex, inputValue }) => (
        <div className="tag-input">
          <div className="tag-input__search">
            <input
              {...getInputProps({
                onKeyDown: (evt) => handleKeyDown(evt, highlightedIndex, inputValue),
                placeholder: t('designPanel.searchPlaceholder'),
              })}
              className={cn('tag-input__input', {
                'tag-input__input--filled': Boolean(inputValue),
              })}
            />
            {inputValue && (
              <button
                type="button"
                onClick={() => search('')}
                className="tag-input__button close"
                title={t('configuration.masks.settingsPanel.reset')}
              >
                <Close width=".75em" height=".75em" />
              </button>
            )}
            <button
              type="button"
              className="tag-input__button search"
              onClick={() => search(inputValue)}
              title={t('designPanel.search.find')}
            >
              <Search width="1.5em" height="1.5em" />
            </button>
          </div>
          <ul {...getMenuProps()} className={cn('tag-input__list', { open: isOpen })}>
            {isOpen && Items(getItemProps, highlightedIndex)}
          </ul>
        </div>
      )}
    </Downshift>
  );
};
